/**
 * RFC-114 LoR4-6 — Scope Manager (pure, no React / Three.js imports)
 *
 * Manages the parent-child layer hierarchy for interior navigation at LoR4+.
 * LoR metadata is read from layer.style (stored via gTwin scene item style field).
 *
 * Architecture reference: MULTI_SCALE_LOR_ARCHITECTURE.md §3 (Scene-Item Extensions)
 *
 * Backward-compatible: layers without lor metadata are unaffected.
 */
import type { Layer } from '@geosoft/viewer-sdk';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Extended LoR state for LoR4+ (interior) scopes. */
export interface LoRScopeState {
  /** ID of the layer currently in scope (null = no explicit scope). */
  activeLayerId: string | null;
  /** LoR level of the active scope (0-6). */
  activeLoR: number;
  /** ID of the parent layer (null = top-level). */
  parentLayerId: string | null;
  /** IDs of child layers that are unlocked at the next LoR level. */
  childLayerIds: string[];
  /** Human-readable label for the active scope. */
  label: string;
}

export type DisciplineId = 'arch' | 'struct' | 'mech' | 'elec' | 'plumb';

export const ALL_DISCIPLINES: DisciplineId[] = ['arch', 'struct', 'mech', 'elec', 'plumb'];

export const DISCIPLINE_LABELS: Record<DisciplineId, string> = {
  arch: 'Arch',
  struct: 'Struct',
  mech: 'Mech',
  elec: 'Elec',
  plumb: 'Plumb',
};

// ---------------------------------------------------------------------------
// Metadata helpers — read LoR fields from layer.style
// ---------------------------------------------------------------------------

/** Read lor level from layer.style (stored as style.lor in gTwin scene item). */
export function getLayerLoR(layer: Layer): number | null {
  const s = layer.style;
  if (!s) return null;
  const lor = s['lor'];
  if (typeof lor === 'number') return lor;
  return null;
}

/** Read childLayerIds from layer.style. */
export function getChildLayerIds(layer: Layer): string[] {
  const s = layer.style;
  if (!s) return [];
  const ids = s['childLayerIds'];
  if (Array.isArray(ids)) return ids.filter((id): id is string => typeof id === 'string');
  return [];
}

/** Read containerOfLayerId from layer.style. */
export function getContainerOfLayerId(layer: Layer): string | null {
  const s = layer.style;
  if (!s) return null;
  const id = s['containerOfLayerId'];
  if (typeof id === 'string' && id.length > 0) return id;
  return null;
}

/** Read storey metadata from layer.style (integer or null). */
export function getLayerStorey(layer: Layer): number | null {
  const s = layer.style;
  if (!s) return null;
  const storey = s['storey'];
  if (typeof storey === 'number') return storey;
  return null;
}

/** Read discipline tag from layer.style. */
export function getLayerDiscipline(layer: Layer): DisciplineId | null {
  const s = layer.style;
  if (!s) return null;
  const disc = s['discipline'];
  if (typeof disc === 'string') return disc as DisciplineId;
  return null;
}

// ---------------------------------------------------------------------------
// Scope resolution
// ---------------------------------------------------------------------------

/**
 * Resolve the child layers that should be mounted when drilling into a scope.
 * Returns an empty array when the layer has no children.
 */
export function resolveChildScope(catalog: Layer[], layerId: string): Layer[] {
  const parent = catalog.find((l) => l.id === layerId);
  if (!parent) return [];
  const childIds = getChildLayerIds(parent);
  if (childIds.length === 0) return [];
  return catalog.filter((l) => childIds.includes(l.id));
}

/**
 * Resolve the parent layer of the given layer.
 * Returns null when the layer is already at the top-level scope.
 */
export function resolveParentScope(catalog: Layer[], layerId: string): Layer | null {
  const current = catalog.find((l) => l.id === layerId);
  if (!current) return null;
  const parentId = getContainerOfLayerId(current);
  if (!parentId) return null;
  return catalog.find((l) => l.id === parentId) ?? null;
}

/**
 * Returns true when a layer should be visible / mounted at the given LoR level.
 *
 * Rules:
 * - A layer with lor == activeLoR is always visible.
 * - A layer with lor == activeLoR - 1 is the "context keep" (parent shell): visible.
 * - A layer with lor >= activeLoR + 1 is a child-of-child (deeper scope): not yet visible.
 * - A layer without lor metadata follows the default visibility from the gTwin.
 */
export function shouldMountForLoR(layer: Layer, activeLoR: number): boolean {
  const lor = getLayerLoR(layer);
  if (lor === null) return true; // no lor metadata → respect default
  return lor <= activeLoR;
}

/**
 * Find the first LoR3 layer in the catalog that has child layers.
 * Used as the initial "focused building" when entering the LoR4 proof.
 */
export function findFirstDrillableLoR3(catalog: Layer[]): Layer | null {
  return (
    catalog.find((l) => {
      const lor = getLayerLoR(l);
      return lor === 3 && getChildLayerIds(l).length > 0;
    }) ?? null
  );
}

/**
 * Build a LoRScopeState from the active layer and catalog.
 * Pass activeLayerId = null to represent the ambient (distance-based) scope.
 */
export function buildScopeState(
  catalog: Layer[],
  activeLayerId: string | null,
  fallbackLoR: number,
): LoRScopeState {
  if (!activeLayerId) {
    return {
      activeLayerId: null,
      activeLoR: fallbackLoR,
      parentLayerId: null,
      childLayerIds: [],
      label: '',
    };
  }

  const layer = catalog.find((l) => l.id === activeLayerId);
  if (!layer) {
    return {
      activeLayerId,
      activeLoR: fallbackLoR,
      parentLayerId: null,
      childLayerIds: [],
      label: activeLayerId,
    };
  }

  return {
    activeLayerId,
    activeLoR: getLayerLoR(layer) ?? fallbackLoR,
    parentLayerId: getContainerOfLayerId(layer),
    childLayerIds: getChildLayerIds(layer),
    label: layer.label ?? activeLayerId,
  };
}

/**
 * Collect unique storey values from a set of layers (children of the active scope).
 * Returns sorted storey labels like ["B1", "GF", "1", "2"].
 */
export function collectStoreys(layers: Layer[]): string[] {
  const storeys = new Set<number>();
  for (const l of layers) {
    const s = getLayerStorey(l);
    if (s !== null) storeys.add(s);
  }
  const sorted = [...storeys].sort((a, b) => a - b);
  return sorted.map((s) => {
    if (s < 0) return `B${Math.abs(s)}`;
    if (s === 0) return 'GF';
    return String(s);
  });
}

/**
 * Filter a layer set by active disciplines.
 * Returns all layers when activeDisciplines is empty (no filter active).
 */
export function filterByDiscipline(layers: Layer[], activeDisciplines: Set<DisciplineId>): Layer[] {
  if (activeDisciplines.size === 0) return layers;
  return layers.filter((l) => {
    const disc = getLayerDiscipline(l);
    if (!disc) return true; // no discipline tag → always show
    return activeDisciplines.has(disc);
  });
}
